import { computed, reactive, ref } from 'vue';
import { defineStore } from 'pinia';
import type {
  AdminMessageReviewPage,
  AgentMessageReviewDetailView,
  AgentMessageReviewSnapshotView,
} from 'shared';
import { adminApi } from '@/api';

export const useMessageReviewStore = defineStore('message-review', () => {
  const filters = reactive({
    status: 'pending_review',
    severity: '',
    category: '',
    source: '',
    userId: '',
    workspaceId: '',
    agentId: '',
    messageId: '',
  });
  const page = ref(1);
  const limit = ref(20);
  const result = ref<AdminMessageReviewPage>();
  const detail = ref<AgentMessageReviewDetailView>();
  const snapshot = ref<AgentMessageReviewSnapshotView>();
  const loading = ref(false);
  const detailLoading = ref(false);
  const error = ref('');
  const items = computed(() => result.value?.items ?? []);
  const summary = computed(
    () => result.value?.summary ?? { pending: 0, highRisk: 0, last24Hours: 0, modelFailed: 0 },
  );

  function query(): string {
    const search = new URLSearchParams({ page: String(page.value), limit: String(limit.value) });
    for (const [key, value] of Object.entries(filters)) if (value) search.set(key, value);
    return search.toString();
  }
  async function load(): Promise<void> {
    loading.value = true;
    error.value = '';
    try {
      result.value = await adminApi.get<AdminMessageReviewPage>(`/admin/message-reviews?${query()}`);
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : '审查案件加载失败，请稍后重试。';
    } finally {
      loading.value = false;
    }
  }
  async function open(caseId: string): Promise<void> {
    detailLoading.value = true;
    snapshot.value = undefined;
    try {
      detail.value = await adminApi.get<AgentMessageReviewDetailView>(`/admin/message-reviews/${caseId}`);
    } finally {
      detailLoading.value = false;
    }
  }
  async function reveal(caseId: string, reason: string): Promise<void> {
    snapshot.value = await adminApi.post<AgentMessageReviewSnapshotView>(
      `/admin/message-reviews/${caseId}/content`,
      { reason },
    );
  }
  async function decide(caseId: string, decision: 'confirmed' | 'false_positive', reason: string): Promise<void> {
    detail.value = await adminApi.patch<AgentMessageReviewDetailView>(
      `/admin/message-reviews/${caseId}/decision`,
      { decision, reason },
    );
    await load();
  }
  async function retry(caseId: string): Promise<void> {
    await adminApi.post(`/admin/message-reviews/${caseId}/retry`, {});
    await open(caseId);
    await load();
  }
  return { filters, page, limit, result, detail, snapshot, loading, detailLoading, error, items, summary, load, open, reveal, decide, retry };
});
